import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  collectionGroup,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db, storage } from "../../../firebase";
import TechnicianSchedule from "./TechnicianSchedule";
import { buildScheduleAssignments, filterScheduleBookings } from "./TechnicianScheduleUtils";
import EditModal from "./EditModal";
import "./stylesSchedule.css";
import { Button, Modal } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

const TechniciansTable = () => {
  const [technicians, setTechnicians] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState(false);
  const [destinations, setDestinations] = useState({ month: "", status: "loading", data: {} });
  const [selectedTech, setSelectedTech] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const { currentUser } = useAuth();

  // photos for current month: { [techName]: { url, storagePath, updatedAt } }
  const [photosByTech, setPhotosByTech] = useState({});

  // simple viewer modal
  const [photoViewer, setPhotoViewer] = useState({
    open: false,
    url: "",
    tech: "",
  });

  const monthDocId = `${currentYear}-${String(currentMonth + 1).padStart(
    2,
    "0"
  )}`;

  useEffect(() => {
    let cancelled = false;
    setScheduleLoading(true);
    setScheduleError(false);
    const fetchData = async () => {
      var matchingTeam = [];
      // Load all technicians if user is not a technician
      if (currentUser.roles[0] !== "sleep_technician") {
        // Admin: load all technicians
        // const techSnapshot = await getDocs(collection(db, "sleeptechnicians"));
        // const names = techSnapshot.docs.map((doc) => doc.data());
        // setTechnicians(names);

        const teamOrder = [
          "TEAM A",
          "TEAM B",
          "TEAM C",
          "RHG-A",
          "RHG-B",
          "TU",
          "SCORE",
          "BDT",
        ];

        const techSnapshot = await getDocs(collection(db, "sleeptechnicians"));

        let names = techSnapshot.docs.filter((doc) => doc.data().status === "Available").map((doc) => doc.data());

        names = names.sort((a, b) => {
          const indexA = teamOrder.indexOf(a.team);
          const indexB = teamOrder.indexOf(b.team);

          if (indexA !== -1 && indexB !== -1) {
            if (indexA !== indexB) return indexA - indexB;
          } else if (indexA !== -1) {
            return -1;
          } else if (indexB !== -1) {
            return 1;
          } else {
            if (a.team !== b.team) return a.team.localeCompare(b.team, "th");
          }

          // same team → sort by nickname
          return a.nickname.localeCompare(b.nickname, "th");
        });
        // console.log("All Technicians:", names);
        if (cancelled) return;
        setTechnicians(names);
      } else {
        // Technician: load only their own info from Firestore
        const techSnapshot = await getDocs(collection(db, "sleeptechnicians"));
        const matchingTech = techSnapshot.docs
          .map((doc) => doc.data())
          .find((t) => t.name === currentUser.displayName);

        const team = matchingTech && matchingTech.team;
        matchingTeam = techSnapshot.docs
          .map((doc) => doc.data())
          .filter((t) => matchingTech && t.team === team && t.status === "Available");

        if (cancelled) return;

        if (matchingTeam) {
          setTechnicians(matchingTeam);
        } else {
          console.warn("Technician not found in sleeptechnicians collection");
          setTechnicians([]);
        }
      }

      // Load schedule for the current month
      const docId = `${currentYear}-${String(currentMonth + 1).padStart(
        2,
        "0"
      )}`;
      const docRef = doc(db, "schedules", docId);
      const snapshot = await getDoc(docRef);

      if (cancelled) return;
      if (snapshot.exists()) {
        const data = snapshot.data();
        const formatted = {};

        // Filter schedule by technician role
        if (currentUser.roles[0] === "sleep_technician") {
          // console.log("matchingTeam: ",matchingTeam);
          for (const tech of matchingTeam) {
            const techName = tech.name;
            const userSchedule = data[techName] || {};
            for (const day in userSchedule) {
              formatted[`${techName}-${day}`] = userSchedule[day];
            }
          }
          // const userSchedule = data[currentUser.displayName] || {};
          // for (const day in userSchedule) {
          //   formatted[`${currentUser.displayName}-${day}`] = userSchedule[day];
          // }
        } else {
          for (const techName in data) {
            for (const day in data[techName]) {
              formatted[`${techName}-${day}`] = data[techName][day];
            }
          }
        }

        setSchedule(formatted);
      } else {
        setSchedule({});
      }

      // Load photos map for the month
      const photosRef = doc(db, "schedulePhotos", monthDocId);
      const photosSnap = await getDoc(photosRef);
      if (cancelled) return;
      setPhotosByTech(photosSnap.exists() ? photosSnap.data() : {});
    };

    fetchData().catch(() => {
      if (!cancelled) setScheduleError(true);
    }).finally(() => {
      if (!cancelled) setScheduleLoading(false);
    });
    return () => { cancelled = true; };
  }, [currentMonth, currentYear, currentUser, monthDocId]);

  useEffect(() => {
    let cancelled = false;
    setDestinations({ month: monthDocId, status: "loading", data: {} });
    const loadDestinations = async () => {
      const nextMonth = new Date(currentYear, currentMonth + 1, 1);
      const nextMonthId = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}`;
      const snapshots = await Promise.all([
        getDocs(query(collectionGroup(db, "bookings"),
          where("extendedProps.latestSleepStudyDate", ">=", Timestamp.fromDate(new Date(`${monthDocId}-01T00:00:00+07:00`))),
          where("extendedProps.latestSleepStudyDate", "<", Timestamp.fromDate(new Date(`${nextMonthId}-01T00:00:00+07:00`))))),
        // The booking form stores Latest Sleep Study Date as YYYY-MM-DD.
        getDocs(query(collectionGroup(db, "bookings"),
          where("extendedProps.latestSleepStudyDate", ">=", `${monthDocId}-01`),
          where("extendedProps.latestSleepStudyDate", "<", `${nextMonthId}-01`))),
        getDocs(collection(db, "nodes")),
        getDoc(doc(db, "admins", currentUser.displayName)),
      ]);
      const bookings = snapshots.slice(0, 2).reduce((rows, snapshot) => {
        snapshot.docs.forEach((entry) => {
          // Ignore any legacy mirrors outside the active node booking collection.
          if (/^nodes\/[^/]+\/bookings\/[^/]+$/.test(entry.ref.path)) rows.push({ ...entry.data(), id: entry.ref.path });
        });
        return rows;
      }, []);
      const nodes = snapshots[2].docs.map((entry) => ({ ...entry.data(), id: entry.id }));
      const adminData = snapshots[3].exists() ? snapshots[3].data() : {};
      const visible = filterScheduleBookings(bookings, currentUser, nodes, adminData);
      const assignments = buildScheduleAssignments(visible, currentYear, currentMonth, nodes);
      if (!cancelled) setDestinations({
        month: monthDocId, status: "ready",
        data: assignments.destinations,
        caseCounts: assignments.caseCounts,
      });
    };
    loadDestinations().catch(() => {
      if (!cancelled) setDestinations({ month: monthDocId, status: "error", data: {} });
    });
    return () => { cancelled = true; };
  }, [currentMonth, currentYear, currentUser, monthDocId]);

  const handleTechClick = (name) => {
    console.log("Selected Technician:", name);
    setSelectedTech(name);
    setShowModal(true);
  };

  const handleSaveSchedule = async (updates) => {
    const docId = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
    const docRef = doc(db, "schedules", docId);

    try {
      // Fetch current document
      const existingDoc = await getDoc(docRef);
      const currentData = existingDoc.exists() ? existingDoc.data() : {};

      // Extract new data for this technician
      const newTechData = {};
      for (const key in updates) {
        const [, day] = key.split("-");
        newTechData[day] = updates[key]; // e.g. { "10": "N", "15": "OFF" }
      }

      // Update entire technician's data (remove missing days)
      const updatedDoc = {
        ...currentData,
        [selectedTech]: newTechData,
      };

      await setDoc(docRef, updatedDoc);

      // Update local state only for selectedTech
      const localUpdates = {};
      for (const key in schedule) {
        if (!key.startsWith(`${selectedTech}-`)) {
          localUpdates[key] = schedule[key]; // keep others
        }
      }
      for (const day in newTechData) {
        localUpdates[`${selectedTech}-${day}`] = newTechData[day];
      }

      setSchedule(localUpdates);
      setShowModal(false);
    } catch (error) {
      console.error("Error saving schedule:", error);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // ---- NEW: upload & view handlers ----
  const handleUploadPhoto = async (techName, file) => {
    if (!file) return;
    try {
      // use a safe name in Storage paths
      var safeTech = encodeURIComponent(techName);
      var path =
        "schedulePhotos/" +
        monthDocId +
        "/" +
        safeTech +
        "-" +
        Date.now() +
        "-" +
        file.name;

      var storageRefObj = ref(storage, path);
      await uploadBytes(storageRefObj, file);
      var url = await getDownloadURL(storageRefObj);

      // previous entry may be undefined on first upload
      var prevEntry = (photosByTech && photosByTech[techName]) || null;
      var prevPath = (prevEntry && prevEntry.storagePath) || null;

      // delete old file if different
      if (prevPath && prevPath !== path) {
        try {
          await deleteObject(ref(storage, prevPath));
        } catch (e) {
          // ignore if already deleted or not found
        }
      }

      // Save/merge this tech's photo map for the month
      var photosRef = doc(db, "schedulePhotos", monthDocId);
      await setDoc(
        photosRef,
        {
          [techName]: {
            url: url,
            storagePath: path,
            updatedAt: new Date().toISOString(),
          },
        },
        { merge: true }
      );

      // update local state
      setPhotosByTech(function (prev) {
        return Object.assign({}, prev, {
          [techName]: {
            url: url,
            storagePath: path,
            updatedAt: new Date().toISOString(),
          },
        });
      });
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    }
  };

  const handleViewPhoto = (techName) => {
    const url = photosByTech[techName].url;
    if (!url) return;
    setPhotoViewer({ open: true, url, tech: techName });
  };

  return (
    <div style={{ overflow: "hidden" }}>
      <div className="tech-month-navigation" aria-label="Month navigation">
        <Button variant="outline-secondary" aria-label="Previous month" onClick={handlePrevMonth}>‹</Button>
        <h2>{new Date(currentYear, currentMonth).toLocaleDateString("en-GB", { year: "numeric", month: "long" })}</h2>
        <Button variant="outline-secondary" aria-label="Next month" onClick={handleNextMonth}>›</Button>
        <Button variant="outline-secondary" onClick={() => {
          const now = new Date();
          setCurrentMonth(now.getMonth());
          setCurrentYear(now.getFullYear());
        }}>Current Month</Button>
      </div>

      {scheduleLoading ? <div className="p-4" role="status">Loading schedule…</div> : scheduleError ? (
        <div className="alert alert-warning" role="alert">Unable to load the schedule. Please reload to try again.</div>
      ) : <TechnicianSchedule
        key={monthDocId}
        year={currentYear}
        month={currentMonth}
        destinations={destinations.month === monthDocId ? destinations.data : {}}
        caseCounts={destinations.month === monthDocId ? destinations.caseCounts || {} : {}}
        destinationStatus={destinations.month === monthDocId ? destinations.status : "loading"}
        technicians={technicians}
        schedule={schedule}
        onTechClick={handleTechClick}
        photosByTech={photosByTech}
        onUploadPhoto={handleUploadPhoto}
        onViewPhoto={handleViewPhoto}
      />}

      {showModal && (
        <EditModal
          technician={selectedTech}
          onClose={() => setShowModal(false)}
          onSave={handleSaveSchedule}
          year={currentYear}
          month={currentMonth}
          existingSchedule={schedule} // ✅ Pass full schedule object
        />
      )}
        {/* Photo Viewer Modal */}
        <Modal
          show={photoViewer.open}
          onHide={() => setPhotoViewer({ open: false, url: "", tech: "" })}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Photo — {photoViewer.tech} ({monthDocId})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ textAlign: "center" }}>
            {photoViewer.url ? (
              <img
                src={photoViewer.url}
                alt="schedule"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div>No image</div>
            )}
          </Modal.Body>
        </Modal>
    </div>
  );
};

export default TechniciansTable;
